import { useState, useCallback, useRef } from 'react';
import { Select, Card, Typography } from '@/shared/ui/primitives';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { roomAdApi } from '../api/roomAd.api';

const { Text } = Typography;

interface UserOption {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface UserSelectorProps {
  value: number | null;
  onChange: (userId: number | null) => void;
  error?: string;
  required?: boolean;
}

const DEBOUNCE_MS = 300;

export default function UserSelector({ value, onChange, error, required = false }: UserSelectorProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users', 'search', search],
    queryFn: () => roomAdApi.searchUsers(search, 0, 50),
    enabled: true,
  });

  const users: UserOption[] = (data as any)?.content || [];

  const options = users.map((user) => {
    const name = `${user.firstName} ${user.lastName}`.trim();
    const contact = user.phoneNumber || user.email;
    return {
      value: user.id,
      label: contact ? `${name} (${contact})` : name,
      user,
    };
  });

  const handleSearch = useCallback((val: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearch(val);
    }, DEBOUNCE_MS);
  }, []);

  const handleChange = useCallback((val: number | undefined) => {
    if (val == null) {
      setSelectedUser(null);
      onChange(null);
      setSearch('');
      return;
    }
    const found = users.find((u) => u.id === val);
    if (found) {
      setSelectedUser(found);
      onChange(found.id);
    }
  }, [users, onChange]);

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Text strong>
          {t('roomAds.ownerUser')} {required && <Text type="danger">*</Text>}
        </Text>
      </div>

      <Select
        showSearch
        allowClear
        style={{ width: '100%' }}
        placeholder={t('roomAds.searchUser')}
        value={value ?? undefined}
        onChange={handleChange}
        onSearch={handleSearch}
        filterOption={false}
        loading={isLoading}
        options={options}
        status={error ? 'error' : undefined}
        notFoundContent={isLoading ? t('common.loading') : t('common.noData')}
      />

      {error && <Text type="danger" style={{ fontSize: 12 }}>{error}</Text>}

      {selectedUser && (
        <Card size="small" style={{ marginTop: 8, background: '#f0f5ff', borderColor: '#adc6ff' }}>
          <Text strong>{selectedUser.firstName} {selectedUser.lastName}</Text>
          {selectedUser.email && <div><Text type="secondary" style={{ fontSize: 12 }}>{selectedUser.email}</Text></div>}
          {selectedUser.phoneNumber && <div><Text type="secondary" style={{ fontSize: 12 }}>{selectedUser.phoneNumber}</Text></div>}
        </Card>
      )}
    </div>
  );
}

